import {
  buildSupersets,
  calculatePrefixForExercise,
} from "./prefix-calculator";

/**
 * Helpers for the add-exercise modal's workout queue.
 *
 * Queue items are either exercises or section separators:
 *   { kind: "exercise", rawForm, exercise, linkedToPrev }
 *     - rawForm: form snapshot to reload into the modal when editing a card
 *     - exercise: the validated exercise (display + save payload)
 *     - linkedToPrev: superset link with the previous exercise card
 *   { kind: "section", name }
 *
 * The queue is persisted per destination (program session or calendar day)
 * so it survives the in-modal flows that trigger a full page refresh
 * (Create new exercise, RM update) and restores only for the same slot.
 */

const STORAGE_PREFIX = "workoutQueue.v1";
const MAX_QUEUE_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export const isQueueExercise = (item) => item?.kind === "exercise";
export const isQueueSection = (item) => item?.kind === "section";

/** Client-only id for React keys and drag-and-drop identity. */
export const generateQueueItemId = () =>
  `wq-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Labels for the queue cards, matching what the session table shows after
 * save: same grouping (buildSupersets) and prefix rules (A for solo runs,
 * A1/A2 for multi-member supersets), restarting at "A" after each section —
 * mirrors computeSectionPrefixes in prefix-calculator.js.
 *
 * @param {Array} items queue items
 * @returns {Array<string|null>} one entry per item; null for sections
 */
export const computeQueueLabels = (items = []) => {
  const labels = new Array(items.length).fill(null);
  let segment = [];

  const flushSegment = () => {
    if (!segment.length) return;
    segment.forEach((exercise, i) => {
      const next = segment[i + 1];
      if (next?.linkedToPrev) exercise.nextSuperset = next._id;
    });
    const supersets = buildSupersets(segment);
    segment.forEach(({ _id, index }) => {
      labels[index] = calculatePrefixForExercise(_id, supersets);
    });
    segment = [];
  };

  items.forEach((item, index) => {
    if (isQueueSection(item)) {
      flushSegment();
      return;
    }
    if (!isQueueExercise(item)) return;
    segment.push({
      _id: String(index),
      index,
      linkedToPrev: item.linkedToPrev,
    });
  });
  flushSegment();

  return labels;
};

/**
 * @param {Object} destination { cycle: {id, program, programWeek, programSession} } or { date: "YYYYMMDD-ish" }
 * @param {string} [athleteId] proxy athlete (coach contexts); "self" otherwise
 */
export const buildQueueStorageKey = (destination, athleteId) => {
  const who = athleteId || "self";
  if (destination?.cycle) {
    const { id, program, programWeek, programSession } = destination.cycle;
    return `${STORAGE_PREFIX}.${who}.cycle.${id}.p${program}.w${programWeek}.s${programSession}`;
  }
  return `${STORAGE_PREFIX}.${who}.cal.${destination?.date}`;
};

export const loadQueue = (key) => {
  if (typeof localStorage === "undefined" || !key) return null;
  try {
    const stored = JSON.parse(localStorage.getItem(key));
    if (!stored || !Array.isArray(stored.items)) return null;
    if (!stored.savedAt || Date.now() - stored.savedAt > MAX_QUEUE_AGE_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return {
      items: stored.items,
      workoutName: stored.workoutName || "",
      // Distinguish "never touched the note row" (undefined, prefill from
      // the server instead) from "touched it, possibly cleared it" (a
      // string, including "") — see add-exercise.js's queueNoteDirty gate.
      note: typeof stored.note === "string" ? stored.note : undefined,
      // Edit Workout only: the saved exercise ids the draft was built from.
      expectedIds: stored.expectedIds,
    };
  } catch (_err) {
    return null;
  }
};

export const persistQueue = (key, items, workoutName, note, expectedIds) => {
  if (typeof localStorage === "undefined" || !key) return;
  try {
    if (!items.length && !workoutName && !note) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(
      key,
      JSON.stringify({
        savedAt: Date.now(),
        workoutName,
        note,
        items,
        expectedIds,
      })
    );
  } catch (_err) {
    // Storage full/unavailable: the queue still works for this page life.
  }
};

export const clearQueue = (key) => {
  if (typeof localStorage === "undefined" || !key) return;
  try {
    localStorage.removeItem(key);
  } catch (_err) {
    // ignore
  }
};

/**
 * Evict every stale/malformed workout-queue entry across all destinations,
 * not just the one this modal instance is about to load. Lazy per-key
 * expiry (loadQueue) only fires for a destination the coach revisits; a
 * coach with many athletes/cycles/calendar days can otherwise accumulate
 * abandoned keys indefinitely. Cheap enough to run on every modal mount.
 */
export const sweepStaleQueues = () => {
  if (typeof localStorage === "undefined") return;
  try {
    const staleKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(`${STORAGE_PREFIX}.`)) continue;
      try {
        const stored = JSON.parse(localStorage.getItem(key));
        if (
          !stored?.savedAt ||
          Date.now() - stored.savedAt > MAX_QUEUE_AGE_MS
        ) {
          staleKeys.push(key);
        }
      } catch (_err) {
        staleKeys.push(key);
      }
    }
    staleKeys.forEach((key) => {
      localStorage.removeItem(key);
    });
  } catch (_err) {
    // ignore
  }
};

/** Total number of working sets across queued exercises. */
export const countQueueSets = (items = []) =>
  items.reduce((total, item) => {
    if (!isQueueExercise(item)) return total;
    return (
      total +
      (item.exercise?.sets || []).reduce(
        (sum, set) => sum + (parseInt(set.sets, 10) || 0),
        0
      )
    );
  }, 0);

export const countQueueExercises = (items = []) =>
  items.filter(isQueueExercise).length;

/**
 * Whether the item at `index` may be superset-linked to the previous one:
 * only between two adjacent exercise cards (never across a section, never
 * for the first item).
 */
// Edit Workout cards that can't be in a superset: complexes and removed cards.
const isLinkable = (item) =>
  isQueueExercise(item) && !item.complexIds && !item.removed;

export const canLinkToPrev = (items, index) =>
  index > 0 && isLinkable(items[index]) && isLinkable(items[index - 1]);

/**
 * Drop superset links that became illegal after a reorder/remove/insert:
 * the first item and any exercise no longer directly preceded by another
 * exercise lose their `linkedToPrev`.
 *
 * @param {Array} items queue items (mutated copies are fine)
 * @returns {Array} new array with sanitized links
 */
export const sanitizeQueueLinks = (items = []) =>
  items.map((item, index) => {
    if (!isQueueExercise(item) || !item.linkedToPrev) return item;
    if (canLinkToPrev(items, index)) return item;
    return { ...item, linkedToPrev: false };
  });

/**
 * Contiguous linked runs ("blocks") in the queue's current order: a section
 * is always its own solitary block; exercises chain into a block for as
 * long as each next item's `linkedToPrev` is true. Mirrors buildSupersets
 * in prefix-calculator.js, but keyed off the queue's positional link flag
 * instead of an id-based `nextSuperset` pointer.
 *
 * @param {Array} items queue items in their current order
 * @returns {Array<Array<string>>} blocks of item ids
 */
const buildQueueBlocks = (items = []) => {
  const blocks = [];
  let current = null;
  items.forEach((item) => {
    if (current && isQueueExercise(item) && item.linkedToPrev) {
      current.push(item.id);
    } else {
      current = [item.id];
      blocks.push(current);
    }
  });
  return blocks;
};

/**
 * Reorder the queue while keeping superset blocks atomic, matching
 * DayEditor's reorderPreservingSupersets: dragging one member carries its
 * whole block to the drop position (in its original internal order), and
 * every other block anchors at its first-seen member so an outside item
 * dropped "into" it lands adjacent instead of splitting it.
 *
 * @param {Array} originalItems queue items before the drag
 * @param {Array<string>} newOrderedIds proposed ids after a naive single-item move
 * @param {string} [draggedId] id of the row that was dragged
 * @returns {Array} queue items reordered with blocks kept contiguous
 */
export const reorderQueuePreservingLinks = (
  originalItems = [],
  newOrderedIds = [],
  draggedId = null
) => {
  const blocks = buildQueueBlocks(originalItems);
  const blockByMember = new Map();
  blocks.forEach((members, index) => {
    for (const member of members) blockByMember.set(member, index);
  });

  const dragged = draggedId == null ? null : String(draggedId);
  const draggedBlock =
    dragged !== null && blockByMember.has(dragged)
      ? blockByMember.get(dragged)
      : null;

  const anchors = new Map();
  newOrderedIds.forEach((id, position) => {
    const key = blockByMember.has(id) ? blockByMember.get(id) : `solo:${id}`;
    if (key === draggedBlock) {
      if (id === dragged) anchors.set(key, position);
    } else if (!anchors.has(key)) {
      anchors.set(key, position);
    }
  });

  const idOrder = [];
  const orderedKeys = [...anchors.keys()].sort(
    (a, b) => anchors.get(a) - anchors.get(b)
  );
  for (const key of orderedKeys) {
    if (typeof key === "number") idOrder.push(...blocks[key]);
    else idOrder.push(key.slice("solo:".length));
  }

  const itemById = new Map(originalItems.map((item) => [item.id, item]));
  return idOrder.map((id) => itemById.get(id)).filter(Boolean);
};

/**
 * Whether a live drop preview would wedge the dragged item inside a
 * *different* block's span (e.g. dropping an outsider between two members
 * of an existing superset).
 *
 * Meant for onDragOver, to refuse the hover target before dnd-kit ever
 * previews the invalid split (dragend can't be prevented after the fact).
 *
 * @param {Array} originalItems queue items before the drag
 * @param {Array<string>} naiveOrder proposed ids after a naive single-item move
 * @param {string} draggedId id of the row being dragged
 * @returns {boolean}
 */
export const wedgesIntoForeignBlock = (
  originalItems,
  naiveOrder,
  draggedId
) => {
  const draggedPos = naiveOrder.indexOf(draggedId);
  if (draggedPos === -1) return false;
  return buildQueueBlocks(originalItems)
    .filter((ids) => ids.length > 1 && !ids.includes(draggedId))
    .some((ids) => {
      const positions = ids.map((id) => naiveOrder.indexOf(id));
      return (
        draggedPos > Math.min(...positions) &&
        draggedPos < Math.max(...positions)
      );
    });
};

/**
 * Whether a live drop preview would permute the dragged item's position
 * relative to one of its own groupmates (e.g. swapping A1/A2 within a
 * two-member superset). The group is otherwise free to move as a unit
 * (see reorderQueuePreservingLinks) — a member carrying its groupmates to
 * an entirely new spot keeps its relative order with all of them and is
 * not flagged; only an actual internal reshuffle is. A permutation that
 * nets to a true no-op (dragged item ends up back where it started) would
 * otherwise leave dnd-kit's live-drag transform stuck mid-swap since
 * nothing in the final order actually changes to trigger a layout reset.
 *
 * @param {Array} originalItems queue items before the drag
 * @param {Array<string>} naiveOrder proposed ids after a naive single-item move
 * @param {string} draggedId id of the row being dragged
 * @returns {boolean}
 */
export const reordersOwnBlock = (originalItems, naiveOrder, draggedId) => {
  const ownBlock = buildQueueBlocks(originalItems).find((ids) =>
    ids.includes(draggedId)
  );
  if (!ownBlock || ownBlock.length < 2) return false;
  const draggedPos = naiveOrder.indexOf(draggedId);
  const originalDraggedIndex = ownBlock.indexOf(draggedId);
  return ownBlock.some((id, index) => {
    if (id === draggedId) return false;
    const wasBefore = index < originalDraggedIndex;
    const isBefore = naiveOrder.indexOf(id) < draggedPos;
    return wasBefore !== isBefore;
  });
};
